import React, { Fragment, useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { forOwn, get, has } from 'lodash-es';
import Form from 'reactstrap/lib/Form';
import { animateScroll } from 'react-scroll';
import { EdmundsAPI } from 'client/data/api/api-client';

/* MODELS */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import {
  ConsumerReviewsEntities,
  ConsumerReviewsModel,
  ConsumerReviewsPaths,
} from 'client/data/models/consumer-reviews';

/* TRACKING */
import { TrackingHandler } from 'client/tracking/handler';
import { EventToolbox } from 'client/utils/event-toolbox';

/* SHARED COMPONENTS */
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';

/* COMPONENTS */
import { VehicleReviewAndDetails } from 'site-modules/write-review/components/steps/vehicle-review-and-details/vehicle-review-and-details';
import { AuthorInfo } from 'site-modules/write-review/components/steps/author-info/author-info';
import { MoreRatingsAndAuthorInfo } from 'site-modules/write-review/components/steps/more-ratings-and-author-info/more-ratings-and-author-info';
import { ThankYouPage } from 'site-modules/write-review/components/steps/thank-you-page/thank-you-page';
import { TryAgain } from 'site-modules/write-review/components/steps/try-again/try-again';

/* UTILS */
import { venomHistory } from 'client/utils/history/venom-history';
import { getQuery } from 'client/utils/location';

/* CONSTANTS */
import {
  EMAIL_NOT_MATCH_ERROR,
  FORM_FIELD_NAMES,
  FORM_VALIDATORS,
  STEP_NUMBER,
  UPDATE_CTA,
} from 'site-modules/shared/constants/write-review/write-review';

import './update-review-form.scss';

const SCROLL_DURATION = 300;
const THANK_YOU_PAGE_QUERY = 'thank-you';

export function UpdateReviewFormUI({
  reviewId,
  fields,
  setFieldInvalid,
  reviewData = null,
  validate,
  resetFields,
  fieldRef,
  validationErrors = {},
  vehicle = null,
  location,
  profileEmail = null,
  setModelValue,
  isAuthenticated = null,
}) {
  const { pathname, search } = location;
  const isThankYouPage = has(getQuery(location), THANK_YOU_PAGE_QUERY);
  const [step, setStep] = useState(isThankYouPage ? STEP_NUMBER.STEP_4 : STEP_NUMBER.STEP_1);
  const [isLoading, setLoading] = useState(false);
  const [previousStep, setPreviousStep] = useState(null);
  const reviewInfo = useRef({
    make: get(vehicle, 'make.slug'),
    model: get(vehicle, 'model.slug'),
    year: get(vehicle, 'year'),
  });

  const goNextStep = useCallback(
    nextStep => {
      animateScroll.scrollToTop({ duration: SCROLL_DURATION });

      const newStep = nextStep || step + 1;

      setPreviousStep(step);
      setStep(newStep);
      setLoading(false);
      EventToolbox.fireTrackAction({
        event_type: 'page_enter',
        event_data: {
          action_cause: 'page_load',
        },
      });

      if (newStep === STEP_NUMBER.STEP_4) {
        venomHistory.push(
          `${pathname}${search}${classnames({ '&': search.length, '?': !search.length })}${THANK_YOU_PAGE_QUERY}`
        );
      }
    },
    [pathname, search, step]
  );

  const componentFieldRef = field => {
    if (!field) {
      return undefined;
    }
    return fieldRef(field.props.name, field);
  };

  const onStepSubmit = useCallback(async () => {
    switch (step) {
      case STEP_NUMBER.STEP_1: {
        setLoading(true);

        return EdmundsAPI.fetchJson(
          `/vehiclereviews/v2/is-review-author/${reviewId}/${fields[FORM_FIELD_NAMES.EMAIL].value()}`
        )
          .then(({ result }) => {
            if (result !== 'true') {
              setFieldInvalid(FORM_FIELD_NAMES.EMAIL, EMAIL_NOT_MATCH_ERROR);
              setLoading(false);
              return;
            }

            resetFields();
            reviewInfo.current = {};
            goNextStep();
          })
          .catch(() => goNextStep(STEP_NUMBER.STEP_TRY_AGAIN));
      }
      case STEP_NUMBER.STEP_2:
      case STEP_NUMBER.STEP_3: {
        forOwn(fields, ({ value }, name) => {
          reviewInfo.current[name] = value();
        });
        setLoading(true);

        return setModelValue(ConsumerReviewsPaths.getCreateAndUpdateReviewIdPath(), ConsumerReviewsModel, {
          ...reviewInfo.current,
          reviewId,
        })
          .then(() => {
            TrackingHandler.fireEvent('wr_save_vehicle_review', { value: UPDATE_CTA });
            resetFields();
            reviewInfo.current = {};
            goNextStep();
          })
          .catch(() => goNextStep(STEP_NUMBER.STEP_TRY_AGAIN));
      }
      case STEP_NUMBER.STEP_TRY_AGAIN: {
        goNextStep(previousStep);
        break;
      }
      // `step` is always one of the values defined in STEP_NUMBER.
      /* istanbul ignore next */
      default: {
        break;
      }
    }

    return Promise.resolve();
  }, [fields, goNextStep, previousStep, resetFields, reviewId, setFieldInvalid, setModelValue, step]);

  if (!reviewData) {
    return null;
  }

  const isTryAgain = step === STEP_NUMBER.STEP_TRY_AGAIN;
  const stepToRender = isTryAgain ? previousStep : step;
  const stepProps = {
    reviewData,
    vehicle,
    validate,
    validationErrors,
    fieldRef: componentFieldRef,
    onSubmit: onStepSubmit,
    isLoading,
    isUpdateForm: true,
  };
  const profileData = {
    email: profileEmail,
    isAuthenticated,
  };

  return (
    <Fragment>
      <Form id="update-review-form" className={classnames('update-review-form', { 'd-none': isTryAgain })}>
        {stepToRender === STEP_NUMBER.STEP_1 && <AuthorInfo {...stepProps} profileData={profileData} />}
        {stepToRender === STEP_NUMBER.STEP_2 && <VehicleReviewAndDetails {...stepProps} />}
        {stepToRender === STEP_NUMBER.STEP_3 && <MoreRatingsAndAuthorInfo {...stepProps} />}
        {stepToRender === STEP_NUMBER.STEP_4 && <ThankYouPage vehicle={vehicle} />}
      </Form>
      {isTryAgain && <TryAgain onSubmit={onStepSubmit} />}
    </Fragment>
  );
}

UpdateReviewFormUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  fields: PropTypes.shape({}).isRequired,
  fieldRef: PropTypes.func.isRequired,
  setFieldInvalid: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  resetFields: PropTypes.func.isRequired,
  setModelValue: PropTypes.func.isRequired,
  reviewId: PropTypes.string.isRequired,
  reviewData: ConsumerReviewsEntities.ReviewData,
  profileEmail: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  validationErrors: PropTypes.shape({}),
};

const mapStateToProps = state => ({
  profileEmail: get(state, 'profile.data.idm.identifiers.email[0]'),
  isAuthenticated: get(state, 'profile.isAuthenticated'),
});

export const UpdateReviewForm = connect(mapStateToProps)(
  connectToModel(formValidation(insiderMethods(UpdateReviewFormUI), FORM_VALIDATORS), {
    reviewData: bindToPath(({ reviewId }) => ConsumerReviewsPaths.buildReviewDataPath(reviewId), ConsumerReviewsModel),
  })
);
